/**
 * GastosOperacionalesTable
 *
 * Vista tabular de los gastos operacionales mensuales de una lavandería AF
 * interna. Reemplaza al modal monolítico anterior (round 2026-05-08): la
 * navegación por meses se vuelve panorámica (1 fila = 1 mes) y la edición
 * es inline vía expandable, en lugar de un popup que oculta el contexto.
 *
 * Diseño:
 *  - Cabecera: contador de meses + botón "+ Nuevo mes" (abre un picker
 *    pequeño que solo pide el período, después la fila aparece y se
 *    auto-expande para que el admin llene los datos).
 *  - Tabla con resumen por fila: Período, Ingresos (read-only del cron),
 *    Servicios totales, Sueldos totales, Materiales totales, Total,
 *    Utilidad (con color), Acciones (eliminar).
 *  - Expandable: form completo del mes (4 servicios + Form.List sueldos
 *    + Form.List materiales + notas + botón Guardar/Cancelar).
 *  - Sin filtro temporal externo: la tabla YA es mensual por diseño (1
 *    fila = 1 mes), así que filtrarla por rango/mes sería redundante.
 *    Round 2026-05-08 (iter 5) — antes recibía `dateRange` del padre y
 *    recortaba localmente; se quitó porque el selector Mes/Rango del
 *    header solo aplica a Ingresos (lavados), no a Gastos y utilidad.
 *
 * Aplica a TODAS las lavanderías del catálogo (AF internas + genérica
 * externa). Hasta iter 23 la genérica no exponía gastos operacionales
 * porque se asumía que el gasto al proveedor externo se anotaba en
 * /finanzas-empresa → Gastos. Round 2026-05-11 (decisión del owner): la
 * genérica también lleva su contabilidad propia para que /finanzas-empresa
 * pueda mostrar utilidad por lavandería sin mezclar fuentes.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  BulbOutlined,
  CalendarOutlined,
  DeleteOutlined,
  FileExcelOutlined,
  FireOutlined,
  HomeOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  SaveOutlined,
  ShoppingOutlined,
  TeamOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import type { FormInstance } from "antd";
import { finanzasEmpresaApi } from "../../../services/finanzasEmpresa.api";
import { getModal, getMsg } from "../../../utils/antdStatic";
import { conIvaFromNeto, netoFromConIva } from "../../../utils/iva";
import { exportToXlsxSingleSheet } from "../../finanzas-empresa/exportXls";
import { ahoraChile } from "../../../utils/fechaChile";
import { AdjuntosPeriodo } from "./AdjuntosLavanderia";
import GastosIndividualesLavanderia, { idResumenLavanderia, idDetalleLavanderia, irAElementoLavanderia, type CategoriaGastoLavanderia, type VinculosResumenLavanderia } from "./GastosIndividualesLavanderia";

const { Text } = Typography;

// ─── Helpers ────────────────────────────────────────────────────────────────

const clp = (v?: number | null) =>
  v != null
    ? new Intl.NumberFormat("es-CL", {
        style: "currency",
        currency: "CLP",
        maximumFractionDigits: 0,
      }).format(v)
    : "—";

// Formatter / parser para los InputNumber: muestra "1.234.567" mientras se
// escribe y persiste el número limpio.
const numFormatter = (v: number | string | undefined) =>
  `${v ?? ""}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
const numParser = (v: string | undefined) =>
  Number((v ?? "").replace(/\./g, ""));

// Suma defensiva: filtra null/undefined/string y devuelve number.
const sumField = (arr: any[] | undefined, key: string) =>
  Array.isArray(arr)
    ? arr.reduce((acc, x) => acc + (Number(x?.[key]) || 0), 0)
    : 0;

// ─── MaterialEditor ────────────────────────────────────────────────────────
//
// Sub-componente de UNA fila de Material dentro del Form.List "materiales".
// Round 2026-05-08 (iter 4) — rediseño UX por pedido del usuario:
//
//   - 5 inputs visibles: cantidad, P. unit neto, P. unit con IVA, Total
//     neto, Total con IVA. Todos opcionales EXCEPTO nombre y total (con
//     IVA — que también se llena automáticamente si el usuario edita el
//     neto).
//   - Autocálculo bidireccional sin loops:
//       neto unit ↔ IVA unit          (factor IVA fijo)
//       neto total ↔ IVA total        (factor IVA fijo)
//       cantidad × P unit IVA → total IVA  (cuando ambos > 0)
//       total IVA ÷ cantidad → P unit IVA  (al editar total con cantidad)
//   - Los inputs neto son DERIVADOS, no campos del form. Lo que persiste
//     al backend es: cantidad_unidades, precio_unitario (con IVA), total
//     (con IVA). Esto permite que el flujo "solo total" funcione sin
//     dejar valores ghost en cantidad/precio_unitario.
//
// Cuando el usuario edita un campo, el setter ignora los watches en
// curso y escribe directamente al form. No usamos useEffect para
// recalcular: cada handler hace el cálculo encadenado al instante.

const MaterialEditor: React.FC<{
  name: number;
  restField: object;
  form: FormInstance;
  onRemove: (name: number) => void;
  index: number;
}> = ({ name, restField, form, onRemove, index }) => {
  // Watches por path (cada uno reactiva al cambio del form):
  const cantidad =
    Form.useWatch(["materiales", name, "cantidad_unidades"], form) || 0;
  const precioUnitConIva =
    Form.useWatch(["materiales", name, "precio_unitario"], form) || 0;
  const totalConIva =
    Form.useWatch(["materiales", name, "total"], form) || 0;

  // Derivados (NO persistidos):
  const precioUnitNeto = precioUnitConIva
    ? netoFromConIva(precioUnitConIva)
    : 0;
  const totalNeto = totalConIva ? netoFromConIva(totalConIva) : 0;

  // ── Setters con encadenamiento de cálculo ─────────────────────────
  const setCantidad = (v: number | null) => {
    const cant = Number(v) || 0;
    form.setFieldValue(["materiales", name, "cantidad_unidades"], cant);
    // Si hay precio unitario, recalcular total = cant × p.
    if (cant > 0 && precioUnitConIva > 0) {
      form.setFieldValue(
        ["materiales", name, "total"],
        Math.round(cant * precioUnitConIva),
      );
    }
  };

  const setPrecioUnitConIva = (v: number | null) => {
    const p = Number(v) || 0;
    form.setFieldValue(["materiales", name, "precio_unitario"], p);
    if (cantidad > 0 && p > 0) {
      form.setFieldValue(
        ["materiales", name, "total"],
        Math.round(cantidad * p),
      );
    }
  };

  const setPrecioUnitNeto = (v: number | null) => {
    const neto = Number(v) || 0;
    setPrecioUnitConIva(Math.round(conIvaFromNeto(neto)));
  };

  const setTotalConIva = (v: number | null) => {
    const t = Number(v) || 0;
    form.setFieldValue(["materiales", name, "total"], t);
    // Si hay cantidad, derivar precio unitario.
    if (cantidad > 0 && t > 0) {
      form.setFieldValue(
        ["materiales", name, "precio_unitario"],
        Math.round(t / cantidad),
      );
    }
  };

  const setTotalNeto = (v: number | null) => {
    const neto = Number(v) || 0;
    setTotalConIva(Math.round(conIvaFromNeto(neto)));
  };

  return (
    <Card
      size="small"
      style={{ marginBottom: 8, background: "#fff" }}
      title={
        <Space>
          <Tag color="blue">#{index + 1}</Tag>
          <Text>Material {index + 1}</Text>
          {totalConIva > 0 && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              · {clp(totalConIva)} (IVA incl.)
            </Text>
          )}
        </Space>
      }
      extra={
        <Tooltip title="Eliminar material">
          <Button
            icon={<DeleteOutlined />}
            type="text"
            danger
            size="small"
            onClick={() => onRemove(name)}
          />
        </Tooltip>
      }
    >
      {/* Fila 1: Nombre + Tienda */}
      <Row gutter={[8, 0]}>
        <Col xs={24} md={12}>
          <Form.Item
            {...restField}
            name={[name, "nombre"]}
            label="Nombre"
            style={{ marginBottom: 8 }}
            rules={[
              { required: true, message: "El nombre es obligatorio" },
            ]}
          >
            <Input placeholder="Ej. Detergente líquido" />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            {...restField}
            name={[name, "tienda"]}
            label="Tienda (opcional)"
            style={{ marginBottom: 8 }}
          >
            <Input placeholder="Ej. Líder" />
          </Form.Item>
        </Col>
      </Row>

      {/* Fila 2: Cantidad + Precio unitario (neto / con IVA) */}
      <Row gutter={[8, 0]}>
        <Col xs={24} md={6}>
          <Form.Item
            {...restField}
            name={[name, "cantidad_unidades"]}
            label="Cantidad"
            style={{ marginBottom: 8 }}
            tooltip="Opcional. Si la cargas junto con un precio unitario, el total se calcula solo."
          >
            <InputNumber
              min={0}
              style={{ width: "100%" }}
              placeholder="0"
              onChange={(v) => setCantidad(v as number | null)}
            />
          </Form.Item>
        </Col>
        <Col xs={12} md={9}>
          {/* Input "neto" — DERIVADO. Mostramos `precioUnitNeto` calculado
              desde el form state; al editar, encadenamos a setPrecioUnitConIva
              que escribe `precio_unitario` (la fuente de verdad). */}
          <Form.Item
            label="P. unit. NETO (opcional)"
            style={{ marginBottom: 8 }}
          >
            <InputNumber
              min={0}
              prefix="$"
              style={{ width: "100%" }}
              placeholder="0"
              value={precioUnitNeto || null}
              onChange={(v) => setPrecioUnitNeto(v as number | null)}
              formatter={numFormatter}
              parser={numParser}
            />
          </Form.Item>
        </Col>
        <Col xs={12} md={9}>
          {/* Input "con IVA" — el field PERSISTIDO `precio_unitario`. */}
          <Form.Item
            {...restField}
            name={[name, "precio_unitario"]}
            label="P. unit. CON IVA (opcional)"
            style={{ marginBottom: 8 }}
          >
            <InputNumber
              min={0}
              prefix="$"
              style={{ width: "100%" }}
              placeholder="0"
              onChange={(v) => setPrecioUnitConIva(v as number | null)}
              formatter={numFormatter}
              parser={numParser}
            />
          </Form.Item>
        </Col>
      </Row>

      {/* Fila 3: Total NETO + Total CON IVA (uno de los dos es obligatorio) */}
      <div
        style={{
          background: "#f0f9ff",
          border: "1px solid #bae6fd",
          borderRadius: 8,
          padding: 10,
          marginTop: 4,
        }}
      >
        <Row gutter={[8, 0]}>
          <Col xs={12}>
            <Form.Item
              label={
                <span style={{ fontWeight: 600 }}>Total NETO (opcional)</span>
              }
              style={{ marginBottom: 0 }}
            >
              <InputNumber
                min={0}
                prefix="$"
                style={{ width: "100%" }}
                placeholder="0"
                value={totalNeto || null}
                onChange={(v) => setTotalNeto(v as number | null)}
                formatter={numFormatter}
                parser={numParser}
              />
            </Form.Item>
          </Col>
          <Col xs={12}>
            <Form.Item
              {...restField}
              name={[name, "total"]}
              label={
                <span style={{ fontWeight: 600 }}>
                  Total CON IVA <span style={{ color: "#dc2626" }}>*</span>
                </span>
              }
              style={{ marginBottom: 0 }}
              rules={[
                {
                  validator: (_, value) =>
                    Number(value) > 0
                      ? Promise.resolve()
                      : Promise.reject(
                          new Error("Ingresa el total (neto o con IVA)"),
                        ),
                },
              ]}
            >
              <InputNumber
                min={0}
                prefix="$"
                style={{ width: "100%" }}
                placeholder="0"
                onChange={(v) => setTotalConIva(v as number | null)}
                formatter={numFormatter}
                parser={numParser}
              />
            </Form.Item>
          </Col>
        </Row>
      </div>
    </Card>
  );
};

// ─── Tipos ──────────────────────────────────────────────────────────────────

interface GastoRow {
  id: number;
  revision: string;
  lavanderia_id: number;
  periodo: string; // "YYYY-MM-DD" (primer día del mes)
  agua?: number | null;
  gas?: number | null;
  arriendo?: number | null;
  luz?: number | null;
  sueldos?: { nombre?: string; monto?: number; detalle_id?: number }[];
  materiales?: {
    nombre?: string;
    tienda?: string;
    cantidad_unidades?: number;
    precio_unitario?: number;
    cantidad_packs?: number;
    precio_por_pack?: number;
    total?: number;
    detalle_id?: number;
  }[];
  descripcion?: string | null;
  ingresos?: number | null;
  total_gastos?: number | null;
}

interface Props {
  lavanderiaId: number;
  lavanderiaName: string;
}

// ─── Sub-componente: form de edición inline ────────────────────────────────
// Vive dentro del expandable de la tabla. Tiene su propia FormInstance
// para no contaminar el form de Lavados (que vive en el padre).

export const ExpandedRowEditor: React.FC<{
  row: GastoRow;
  onSaved: () => void;
  onCancel: () => void;
  onRefresh: () => void;
}> = ({ row, onSaved, onCancel, onRefresh }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [resumenEditado, setResumenEditado] = useState(false);
  const [vinculos, setVinculos] = useState<VinculosResumenLavanderia | null>(null);
  const recibirVinculos = useCallback((v: VinculosResumenLavanderia | null) => setVinculos(v), []);
  const periodo = row.periodo.slice(0, 7);
  const verDetalle = (id: number) => irAElementoLavanderia(idDetalleLavanderia(row.lavanderia_id, periodo, id));
  const marcaCategoria = (categoria: CategoriaGastoLavanderia) => {
    if (!vinculos || resumenEditado) return null;
    const v = vinculos[categoria];
    if (!v) return null;
    return <Space size={4} wrap style={{ marginTop: 4 }}>
      {v.ids.length > 0 && <Button type="link" size="small" style={{ padding: 0, height: "auto" }} onClick={() => verDetalle(v.ids[0])}>{v.ids.length} {v.ids.length === 1 ? "gasto detallado" : "gastos detallados"}</Button>}
      {v.historico > 0 && <Tag>Sin detalle: {clp(v.historico)}</Tag>}
    </Space>;
  };
  const marcaFila = (id?: number, existe = true) => {
    if (!vinculos || resumenEditado || !existe) return null;
    if (!id) return <Tag>Sin detalle individual</Tag>;
    if (!Object.values(vinculos).some((v) => v?.ids.includes(Number(id)))) return <Tag>Detalle no disponible</Tag>;
    return <Button type="link" size="small" onClick={() => verDetalle(Number(id))}>Ver gasto individual #{id}</Button>;
  };

  // Hidratar al montar / cuando cambia el row (re-expand de otra fila).
  useEffect(() => {
    setVinculos(null);
    form.setFieldsValue({
      agua: row.agua ?? null,
      gas: row.gas ?? null,
      arriendo: row.arriendo ?? null,
      luz: row.luz ?? null,
      sueldos: row.sueldos ?? [],
      materiales: row.materiales ?? [],
      descripcion: row.descripcion ?? "",
    });
    setResumenEditado(false);
  }, [row, form]);

  // Watches para los totales en vivo. Importante: si no usamos
  // useWatch, el resumen no se actualiza al editar inputs.
  const agua = Form.useWatch("agua", form) || 0;
  const gas = Form.useWatch("gas", form) || 0;
  const arriendo = Form.useWatch("arriendo", form) || 0;
  const luz = Form.useWatch("luz", form) || 0;
  const sueldos: { nombre?: string; monto?: number; detalle_id?: number }[] = Form.useWatch("sueldos", form) || [];
  const materiales: any[] = Form.useWatch("materiales", form) || [];

  const totalServicios = agua + gas + arriendo + luz;
  const totalSueldos = useMemo(
    () => sumField(sueldos, "monto"),
    [sueldos],
  );
  // Total de materiales: leemos directamente `m.total` (campo persistido,
  // CON IVA). Cada MaterialEditor mantiene `total` sincronizado con el
  // resto: si el user llena cantidad+precio se calcula; si llena total
  // directamente, vive tal cual.
  const totalMateriales = useMemo(
    () => sumField(materiales, "total"),
    [materiales],
  );

  const total = totalServicios + totalSueldos + totalMateriales;
  const ingresos = row.ingresos ?? 0;
  const utilidad = ingresos - total;

  const handleSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      // Cada material ya viene con `total` (con IVA) sincronizado por
      // MaterialEditor — sea autocalculado desde cantidad×precio o
      // ingresado directamente. Solo limpiamos campos legacy que el form
      // pudo arrastrar de records viejos (`cantidad_packs`,
      // `precio_por_pack`) para no persistirlos al backend nuevo.
      const materialesPersist = (values.materiales || []).map((m: any) => {
        const { cantidad_packs: _cp, precio_por_pack: _ppp, ...rest } = m;
        return rest;
      });
      // Round 2026-05-08: apiFetch retorna `null` ante error de red
      // (ERR_NETWORK_CHANGED, timeout, etc.) y muestra el toast de
      // error por su cuenta. Si llamamos `onSaved()` igual, la fila se
      // colapsa y el admin pierde lo que estaba editando — peor: cree
      // que se guardó cuando NO. Solo llamamos onSaved si tenemos
      // confirmación del backend.
      const res = await finanzasEmpresaApi.upsertGastosOperacionales({
        ...values,
        materiales: materialesPersist,
        lavanderia_id: row.lavanderia_id,
        periodo: row.periodo,
        revision: row.revision,
      });
      if (res?.data?.id) {
        onSaved();
      }
      // Si res es null, el toast de error ya se mostró desde apiFetch;
      // dejamos la fila expandida con los datos del form intactos para
      // que el admin pueda reintentar sin re-tipear todo.
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <GastosIndividualesLavanderia key={`${row.id}-${row.revision}`} lavanderiaId={row.lavanderia_id} periodo={periodo} onChanged={onRefresh} disabled={resumenEditado} onVinculos={recibirVinculos} />
    <Form form={form} layout="vertical" size="middle" onValuesChange={() => setResumenEditado(true)}>
      <AdjuntosPeriodo key={`${row.id}-${row.revision}`} lavanderiaId={row.lavanderia_id} periodo={row.periodo.slice(0, 7)} />
      <Alert type="info" showIcon style={{ marginBottom: 12 }} message="Resumen mensual" description="Estos importes incluyen los gastos individuales y el saldo anterior. Para editar o eliminar un gasto detallado, usa la sección superior." />
      {/* ─── Servicios básicos ───────────────────────────────────────── */}
      <Card
        size="small"
        style={{ marginBottom: 12, background: "#fafafa" }}
        title={
          <Space>
            <ThunderboltOutlined />
            <span>Servicios básicos</span>
            {totalServicios > 0 && (
              <Tag color="default">{clp(totalServicios)}</Tag>
            )}
          </Space>
        }
      >
        <Row gutter={[12, 0]}>
          <Col xs={12} md={6} id={idResumenLavanderia(row.lavanderia_id, periodo, "agua")} tabIndex={-1}>
            <Form.Item
              name="agua"
              label={
                <Space size={4}>
                  <ThunderboltOutlined style={{ color: "#1677ff" }} />
                  Agua
                </Space>
              }
              style={{ marginBottom: 0 }}
            >
              <InputNumber
                min={0}
                prefix="$"
                style={{ width: "100%" }}
                formatter={numFormatter}
                parser={numParser}
                placeholder="0"
              />
            </Form.Item>
            {marcaCategoria("agua")}
          </Col>
          <Col xs={12} md={6} id={idResumenLavanderia(row.lavanderia_id, periodo, "gas")} tabIndex={-1}>
            <Form.Item
              name="gas"
              label={
                <Space size={4}>
                  <FireOutlined style={{ color: "#fa541c" }} />
                  Gas
                </Space>
              }
              style={{ marginBottom: 0 }}
            >
              <InputNumber
                min={0}
                prefix="$"
                style={{ width: "100%" }}
                formatter={numFormatter}
                parser={numParser}
                placeholder="0"
              />
            </Form.Item>
            {marcaCategoria("gas")}
          </Col>
          <Col xs={12} md={6} id={idResumenLavanderia(row.lavanderia_id, periodo, "luz")} tabIndex={-1}>
            <Form.Item
              name="luz"
              label={
                <Space size={4}>
                  <BulbOutlined style={{ color: "#faad14" }} />
                  Luz
                </Space>
              }
              style={{ marginBottom: 0 }}
            >
              <InputNumber
                min={0}
                prefix="$"
                style={{ width: "100%" }}
                formatter={numFormatter}
                parser={numParser}
                placeholder="0"
              />
            </Form.Item>
            {marcaCategoria("luz")}
          </Col>
          <Col xs={12} md={6} id={idResumenLavanderia(row.lavanderia_id, periodo, "arriendo")} tabIndex={-1}>
            <Form.Item
              name="arriendo"
              label={
                <Space size={4}>
                  <HomeOutlined style={{ color: "#52c41a" }} />
                  Arriendo
                </Space>
              }
              style={{ marginBottom: 0 }}
            >
              <InputNumber
                min={0}
                prefix="$"
                style={{ width: "100%" }}
                formatter={numFormatter}
                parser={numParser}
                placeholder="0"
              />
            </Form.Item>
            {marcaCategoria("arriendo")}
          </Col>
        </Row>
      </Card>

      {/* ─── Sueldos ─────────────────────────────────────────────────── */}
      <Card
        size="small"
        id={idResumenLavanderia(row.lavanderia_id, periodo, "sueldos")}
        tabIndex={-1}
        style={{ marginBottom: 12, background: "#fafafa" }}
        title={
          <Space>
            <TeamOutlined />
            <span>Sueldos</span>
            <Tag color="default">
              {sueldos.length} {sueldos.length === 1 ? "persona" : "personas"}
            </Tag>
            {totalSueldos > 0 && <Tag color="purple">{clp(totalSueldos)}</Tag>}
          </Space>
        }
        extra={
          <Tooltip title="Pagos a personal asignado a esta lavandería (planchadora, encargada, ayudante, etc.).">
            <InfoCircleOutlined style={{ color: "#8c8c8c" }} />
          </Tooltip>
        }
      >
        {marcaCategoria("sueldos")}
        <Form.List name="sueldos">
          {(fields, { add, remove }) => (
            <>
              {fields.length === 0 && (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <Text type="secondary">Sin sueldos registrados</Text>
                  }
                  style={{ margin: "4px 0 8px" }}
                />
              )}
              {fields.map(({ key, name, ...restField }, idx) => (
                <Row
                  key={key}
                  gutter={[8, 0]}
                  align="middle"
                  style={{ marginBottom: 6 }}
                >
                  <Col flex="32px">
                    <Tag color="blue" style={{ margin: 0 }}>
                      #{idx + 1}
                    </Tag>
                  </Col>
                  <Col flex="1 1 240px">
                    <Form.Item
                      {...restField}
                      name={[name, "nombre"]}
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: "Falta el nombre" }]}
                    >
                      <Input placeholder="Nombre y apellido" />
                    </Form.Item>
                  </Col>
                  <Col flex="0 0 200px">
                    <Form.Item
                      {...restField}
                      name={[name, "monto"]}
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: "Falta el monto" }]}
                    >
                      <InputNumber
                        min={0}
                        placeholder="Monto"
                        prefix="$"
                        style={{ width: "100%" }}
                        formatter={numFormatter}
                        parser={numParser}
                      />
                    </Form.Item>
                  </Col>
                  <Col flex="32px">
                    <Tooltip title="Eliminar sueldo">
                      <Button
                        icon={<DeleteOutlined />}
                        type="text"
                        danger
                        onClick={() => remove(name)}
                      />
                    </Tooltip>
                  </Col>
                  <Col xs={24} style={{ paddingLeft: 40 }}>{marcaFila(sueldos[idx]?.detalle_id, Boolean(sueldos[idx]?.nombre || sueldos[idx]?.monto))}</Col>
                </Row>
              ))}
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={() => add({})}
                block
                style={{ marginTop: fields.length === 0 ? 0 : 4 }}
              >
                Agregar sueldo
              </Button>
            </>
          )}
        </Form.List>
      </Card>

      {/* ─── Materiales ──────────────────────────────────────────────── */}
      <Card
        size="small"
        id={idResumenLavanderia(row.lavanderia_id, periodo, "materiales")}
        tabIndex={-1}
        style={{ marginBottom: 12, background: "#fafafa" }}
        title={
          <Space>
            <ShoppingOutlined />
            <span>Materiales</span>
            <Tag color="default">
              {materiales.length}{" "}
              {materiales.length === 1 ? "ítem" : "ítems"}
            </Tag>
            {totalMateriales > 0 && (
              <Tag color="purple">{clp(totalMateriales)}</Tag>
            )}
          </Space>
        }
        extra={
          <Tooltip
            title={
              <>
                Insumos comprados durante el mes. Solo el <b>nombre</b> y el{" "}
                <b>total</b> son obligatorios. Si llenas cantidad + precio
                unitario, el total se autocalcula. Cualquier valor neto / con
                IVA se sincroniza con el otro automáticamente (factor 1.19).
              </>
            }
          >
            <InfoCircleOutlined style={{ color: "#8c8c8c" }} />
          </Tooltip>
        }
      >
        {marcaCategoria("materiales")}
        <Form.List name="materiales">
          {(fields, { add, remove }) => (
            <>
              {fields.length === 0 && (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <Text type="secondary">Sin materiales registrados</Text>
                  }
                  style={{ margin: "4px 0 8px" }}
                />
              )}
              {fields.map(({ key, name, ...restField }, idx) => (
                <div key={key}>
                  <MaterialEditor name={name} restField={restField} form={form} onRemove={remove} index={idx} />
                  {marcaFila(materiales[idx]?.detalle_id, Boolean(materiales[idx]?.nombre || materiales[idx]?.total))}
                </div>
              ))}
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={() => add({})}
                block
                style={{ marginTop: fields.length === 0 ? 0 : 4 }}
              >
                Agregar material
              </Button>
            </>
          )}
        </Form.List>
      </Card>

      {/* ─── Notas + Resumen + Acciones ─────────────────────────────── */}
      <Card size="small" style={{ marginBottom: 12, background: "#fafafa" }}>
        <Form.Item
          name="descripcion"
          label="Notas / observaciones"
          style={{ marginBottom: 12 }}
        >
          <Input.TextArea
            rows={2}
            placeholder="Observaciones del mes (gastos imprevistos, ajustes, etc.)"
          />
        </Form.Item>

        <Row gutter={[16, 8]} style={{ marginBottom: 8 }}>
          <Col xs={24} sm={8}>
            <Statistic
              title="Ingresos del mes"
              value={ingresos}
              valueStyle={{ color: "#3f8600", fontSize: 18 }}
              formatter={(v) => clp(Number(v))}
            />
          </Col>
          <Col xs={24} sm={8}>
            <Statistic
              title="Total gastos"
              value={total}
              valueStyle={{ color: "#cf1322", fontSize: 18 }}
              formatter={(v) => clp(Number(v))}
            />
          </Col>
          <Col xs={24} sm={8}>
            <Statistic
              title="Utilidad estimada"
              value={utilidad}
              valueStyle={{
                color: utilidad >= 0 ? "#3f8600" : "#cf1322",
                fontSize: 20,
                fontWeight: 700,
              }}
              formatter={(v) => clp(Number(v))}
            />
          </Col>
        </Row>

        {ingresos === 0 && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 8 }}
            message="Ingresos pendientes"
            description="El cron mensual calcula los ingresos cobrados a propiedades. Si aparece $0 todavía no se cargaron lavados para esta lavandería en este período."
          />
        )}
        {utilidad < 0 && total > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 8 }}
            message="Mes con pérdida"
            description="Los gastos superan los ingresos registrados."
          />
        )}

        <div style={{ textAlign: "right" }}>
          <Space>
            <Button onClick={onCancel}>Cancelar</Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={saving}
              onClick={handleSave}
            >
              Guardar mes
            </Button>
          </Space>
        </div>
      </Card>
    </Form>
    </>
  );
};

// ─── Componente principal ───────────────────────────────────────────────────

const GastosOperacionalesTable: React.FC<Props> = ({
  lavanderiaId,
  lavanderiaName,
}) => {
  const [rows, setRows] = useState<GastoRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedKey, setExpandedKey] = useState<number | null>(null);

  // Modal "Nuevo mes": pide solo el período. El upsert crea la fila con
  // todos los gastos en 0; después la fila se auto-expande para que el
  // admin la complete inline.
  const [newMonthOpen, setNewMonthOpen] = useState(false);
  const [newMonthPeriodo, setNewMonthPeriodo] = useState<Dayjs | null>(null);
  const [newMonthSaving, setNewMonthSaving] = useState(false);

  const fetchRows = useCallback(async () => {
    if (!lavanderiaId) return;
    setLoading(true);
    try {
      const res = await finanzasEmpresaApi.getGastosOperacionales(
        lavanderiaId,
      );
      setRows(res?.data ?? []);
    } finally {
      setLoading(false);
    }
  }, [lavanderiaId]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  // Round 2026-05-08 (iter 5): se eliminó el filtro local por dateRange.
  // La tabla ya es mensual por diseño (1 fila = 1 mes), así que un
  // recorte por rango/mes encima sería redundante (y confuso: ¿qué
  // significa "mes de mayo" cuando la fila ES mayo?). El selector
  // temporal del header pasó a vivir dentro del sub-tab Ingresos donde
  // sí tiene sentido (filtra lavados individuales por fecha).
  //
  // Orden DESC por período fijado en el dataSource, no en la columna.
  // Razón: el usuario pidió que el mes más reciente/futuro quede arriba
  // y que los nuevos meses aparezcan automáticamente al tope al
  // crearse. El backend ya devuelve DESC pero al re-fetchar tras un
  // upsert antd Table puede pisar el orden si la columna tiene
  // `sorter`. Lo hacemos acá para tener garantía sin permitir al user
  // re-ordenar.
  const sortedRows = useMemo(
    () => [...rows].sort((a, b) => b.periodo.localeCompare(a.periodo)),
    [rows],
  );

  // ─── Export XLS ───────────────────────────────────────────────────────
  // Round 2026-05-08 (iter 6): exporta la vista resumen (1 fila por mes)
  // con los mismos campos calculados que muestra la tabla. NO incluye
  // sueldos/materiales detallados — para ese nivel de detalle el admin
  // expande la fila correspondiente. Si alguna vez se pide "exportar
  // con detalle por sueldo/material", agregar sheets adicionales con
  // exportToXlsxMultiSheet.
  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    setExporting(true);
    try {
      const exportRows = sortedRows.map((r) => {
        const servicios =
          (Number(r.agua) || 0) +
          (Number(r.gas) || 0) +
          (Number(r.arriendo) || 0) +
          (Number(r.luz) || 0);
        const sueldosTot = sumField(r.sueldos, "monto");
        const materialesTot = sumField(r.materiales, "total");
        const total =
          Number(r.total_gastos) || servicios + sueldosTot + materialesTot;
        const ingresos = Number(r.ingresos) || 0;
        return {
          Período: dayjs(r.periodo).format("MMMM YYYY"),
          Ingresos: ingresos,
          Agua: Number(r.agua) || 0,
          Gas: Number(r.gas) || 0,
          Arriendo: Number(r.arriendo) || 0,
          Luz: Number(r.luz) || 0,
          "Servicios (suma)": servicios,
          Sueldos: sueldosTot,
          Materiales: materialesTot,
          "Total gastos": total,
          Utilidad: ingresos - total,
          Notas: r.descripcion ?? "",
        };
      });
      await exportToXlsxSingleSheet({
        filename: `lavanderia-gastos-${lavanderiaName.replace(/\s+/g, "-").toLowerCase()}-${ahoraChile().format("YYYYMMDD-HHmm")}`,
        sheetName: "Gastos y utilidad",
        rows: exportRows,
      });
      getMsg()?.success(`Exportadas ${exportRows.length} filas`);
    } catch (err: any) {
      getMsg()?.error(err?.message || "Error al exportar");
    } finally {
      setExporting(false);
    }
  };

  const handleCreateMonth = async () => {
    if (!newMonthPeriodo) {
      getMsg()?.warning("Selecciona el mes");
      return;
    }
    const periodoStr = newMonthPeriodo.format("YYYY-MM-01");
    // ¿Ya existe? Si sí, simplemente expandimos esa fila.
    const existing = rows.find((r) => dayjs(r.periodo).isSame(periodoStr, "day"));
    if (existing) {
      setExpandedKey(existing.id);
      setNewMonthOpen(false);
      setNewMonthPeriodo(null);
      getMsg()?.info("El mes ya existe — abierto para editar");
      return;
    }
    setNewMonthSaving(true);
    try {
      // Round 2026-05-08: apiFetch retorna `null` ante error de red
      // (ERR_NETWORK_CHANGED, timeout) y muestra el toast por su cuenta.
      // Antes cerrábamos el modal y limpiábamos el período aunque hubiera
      // fallado — el admin perdía el mes seleccionado y debía re-elegir.
      // Ahora solo cerramos cuando hay confirmación del backend; en caso
      // de error el modal queda abierto con el período intacto para
      // reintentar.
      const res = await finanzasEmpresaApi.upsertGastosOperacionales({
        lavanderia_id: lavanderiaId,
        periodo: periodoStr,
        soloCrear: true,
        agua: 0,
        gas: 0,
        arriendo: 0,
        luz: 0,
        sueldos: [],
        materiales: [],
      });
      if (!res?.data?.id) {
        // Falla de red u otro error — el toast ya lo mostró apiFetch.
        // No tocamos el estado del modal.
        return;
      }
      setExpandedKey(res.data.id);
      setNewMonthOpen(false);
      setNewMonthPeriodo(null);
      await fetchRows();
    } finally {
      setNewMonthSaving(false);
    }
  };

  const handleDelete = (id: number) => {
    const modal = getModal();
    const doDelete = async () => {
      const resultado = await finanzasEmpresaApi.deleteGastosOperacionales(id);
      if (!resultado) return;
      getMsg()?.success("Mes eliminado");
      await fetchRows();
    };
    if (!modal) {
      void doDelete();
      return;
    }
    modal.confirm({
      title: "Eliminar gastos del mes",
      content:
        "Se eliminarán TODOS los gastos operacionales (servicios, sueldos, materiales) de este mes. No se puede deshacer.",
      okText: "Eliminar",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: doDelete,
    });
  };

  // ── Columnas ──────────────────────────────────────────────────────────
  const columns: ColumnsType<GastoRow> = [
    {
      // Sin sorter ni defaultSortOrder: el orden DESC está fijado en
      // el `sortedRows` memo (futuro/actual arriba) y no debe poder
      // cambiarse. Si en el futuro queremos permitir re-ordenar por
      // monto/utilidad, agregar sorter solo en esas otras columnas.
      title: "Período",
      dataIndex: "periodo",
      key: "periodo",
      width: 140,
      render: (v) => (
        <Space size={4}>
          <CalendarOutlined style={{ color: "#1677ff" }} />
          <Text strong>{dayjs(v).format("MMMM YYYY")}</Text>
        </Space>
      ),
    },
    {
      title: "Ingresos",
      dataIndex: "ingresos",
      key: "ingresos",
      width: 120,
      align: "right",
      render: (v) => (
        <Text style={{ color: "#3f8600" }}>{clp(Number(v) || 0)}</Text>
      ),
    },
    {
      title: "Servicios",
      key: "servicios",
      width: 110,
      align: "right",
      render: (_, r) =>
        clp(
          (Number(r.agua) || 0) +
            (Number(r.gas) || 0) +
            (Number(r.luz) || 0) +
            (Number(r.arriendo) || 0),
        ),
    },
    {
      title: "Sueldos",
      key: "sueldos",
      width: 110,
      align: "right",
      render: (_, r) => clp(sumField(r.sueldos, "monto")),
    },
    {
      title: "Materiales",
      key: "materiales",
      width: 120,
      align: "right",
      render: (_, r) => clp(sumField(r.materiales, "total")),
    },
    {
      title: "Total gastos",
      dataIndex: "total_gastos",
      key: "total_gastos",
      width: 130,
      align: "right",
      render: (v) => (
        <Text strong style={{ color: "#cf1322" }}>
          {clp(Number(v) || 0)}
        </Text>
      ),
    },
    {
      title: "Utilidad",
      key: "utilidad",
      width: 130,
      align: "right",
      render: (_, r) => {
        const u = (Number(r.ingresos) || 0) - (Number(r.total_gastos) || 0);
        return (
          <Text strong style={{ color: u >= 0 ? "#3f8600" : "#cf1322" }}>
            {clp(u)}
          </Text>
        );
      },
    },
    {
      title: "Acciones",
      key: "acciones",
      fixed: "right",
      width: 80,
      align: "center",
      render: (_, r) => (
        <Popconfirm
          title="¿Eliminar este mes?"
          onConfirm={() => handleDelete(r.id)}
          okText="Sí"
          cancelText="No"
          okButtonProps={{ danger: true }}
        >
          <Tooltip title="Eliminar mes">
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Tooltip>
        </Popconfirm>
      ),
    },
  ];

  return (
    <Card
      size="small"
      style={{ marginTop: 16, background: "#fff" }}
      title={
        <Space>
          <ShoppingOutlined style={{ color: "#1677ff" }} />
          <span>Gastos operacionales</span>
          <Tag color="blue">{lavanderiaName}</Tag>
          <Text type="secondary" style={{ fontSize: 12 }}>
            ({sortedRows.length} {sortedRows.length === 1 ? "mes" : "meses"}{" "}
            en total)
          </Text>
        </Space>
      }
      extra={
        <Space size={8}>
          <Button
            icon={<FileExcelOutlined />}
            onClick={handleExport}
            loading={exporting}
            disabled={sortedRows.length === 0}
          >
            Exportar a XLS
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setNewMonthOpen(true)}
          >
            Nuevo mes
          </Button>
        </Space>
      }
    >
      <Table<GastoRow>
        rowKey="id"
        size="small"
        loading={loading}
        dataSource={sortedRows}
        columns={columns}
        pagination={false}
        scroll={{ x: 940 }}
        expandable={{
          expandedRowKeys: expandedKey != null ? [expandedKey] : [],
          onExpand: (expanded, record) =>
            setExpandedKey(expanded ? record.id : null),
          // Form completo del mes inline. Cada fila tiene su propio
          // FormInstance — al colapsar y re-expandir se rehidrata desde
          // el `row` del state (que se refresca tras cada save).
          expandedRowRender: (record) => (
            <ExpandedRowEditor
              row={record}
              onRefresh={() => { void fetchRows(); }}
              onSaved={() => {
                getMsg()?.success("Mes guardado");
                setExpandedKey(null);
                fetchRows();
              }}
              onCancel={() => setExpandedKey(null)}
            />
          ),
          rowExpandable: () => true,
        }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Sin meses registrados aún. Crea el primero con el botón de arriba."
            />
          ),
        }}
      />

      {/* ─── Modal mini: nuevo mes ──────────────────────────────────── */}
      <Modal
        open={newMonthOpen}
        title="Nuevo mes de gastos operacionales"
        onOk={handleCreateMonth}
        onCancel={() => {
          setNewMonthOpen(false);
          setNewMonthPeriodo(null);
        }}
        okText="Crear"
        cancelText="Cancelar"
        confirmLoading={newMonthSaving}
        width={420}
      >
        <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
          Selecciona el mes contable. La fila se creará vacía y se abrirá
          automáticamente para que llenes los datos.
        </Text>
        <DatePicker
          picker="month"
          format="MMMM YYYY"
          style={{ width: "100%" }}
          value={newMonthPeriodo}
          onChange={(v) => setNewMonthPeriodo(v)}
          placeholder="Seleccionar mes"
        />
      </Modal>
    </Card>
  );
};

export default GastosOperacionalesTable;
